import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';

import Box from '../box/box.jsx';
import Button from '../button/button.jsx';
import styles from './connection-modal.css';

const SerialStep = props => {
    const [baudRate, setBaudRate] = useState(115200);
    const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, connecting, connected
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState(null);
    const [connectionType, setConnectionType] = useState('real'); // 'real' or 'mock'
    const isMountedRef = useRef(true);

    // ESP32 extension kontrolü
    const isESP32Extension = props.extensionId === 'serialExample';

    // Component unmount olduğunda state update'lerini engelle
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const handleConnectMock = async () => {
        if (!isMountedRef.current) return;
        
        setIsConnecting(true);
        setConnectionStatus('connecting');
        setError(null);

        try {
            console.log('Mock ESP32 bağlantısı başlatılıyor...');
            
            // Mock bağlantı simülasyonu
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            if (isMountedRef.current) {
                setConnectionStatus('connected');
                
                // Parent component'e mock bağlantı başarılı olduğunu bildir
                if (props.onConnected) {
                    props.onConnected();
                }
            }

        } catch (err) {
            console.error('Mock bağlantı hatası:', err);
            
            if (isMountedRef.current) {
                setConnectionStatus('disconnected');
                setError('Mock bağlantı hatası: ' + err.message);
            }
        } finally {
            if (isMountedRef.current) {
                setIsConnecting(false);
            }
        }
    };

    const handleConnect = async () => {
        if (!isMountedRef.current) return;
        
        // ESP32 extension için mock bağlantı seçeneği
        if (isESP32Extension && connectionType === 'mock') {
            return handleConnectMock();
        }
        
        setIsConnecting(true);
        setConnectionStatus('connecting');
        setError(null);

        // Browser ve API desteği kontrolü
        console.log('User Agent:', navigator.userAgent);
        console.log('Vendor:', navigator.vendor);
        console.log('navigator.serial mevcut mu?', !!navigator.serial);
        console.log('navigator.bluetooth mevcut mu?', !!navigator.bluetooth);

        try {
            if (!navigator.serial) {
                throw new Error('Web Serial API desteklenmiyor. Chrome 89+ veya Edge 89+ kullanın.');
            }

            if (!props.isSerialSupported) {
                throw new Error('Seri port desteği mevcut değil. HTTPS veya localhost kullanın.');
            }

            console.log('Web Serial API destekleniyor, port seçimi başlatılıyor...');
            
            // Başarılı bağlantıyı parent component'e bildir
            if (props.onConnectSerial) {
                await props.onConnectSerial(baudRate);
            }
            
            if (isMountedRef.current) {
                setConnectionStatus('connected');
            }

        } catch (err) {
            console.error('Seri port bağlantı hatası:', err);
            
            if (isMountedRef.current) {
                setConnectionStatus('disconnected');
                
                if (err.name === 'NotFoundError') {
                    setError('Hiçbir seri port seçilmedi. Lütfen bir cihaz seçin.');
                } else if (err.name === 'SecurityError') {
                    setError('Güvenlik hatası: HTTPS veya localhost kullanın. Ayrıca tarayıcıda experimental features etkinleştirilmiş olmalı.');
                } else if (err.name === 'NetworkError') {
                    setError('Port zaten kullanımda veya erişilemiyor. Cihazı yeniden bağlamayı deneyin.');
                } else if (err.name === 'NotSupportedError') {
                    setError('Bu cihaz desteklenmiyor veya sürücü sorunu var.');
                } else {
                    setError(err.message || 'Bağlantı hatası oluştu. Lütfen tekrar deneyin.');
                }
            }
        } finally {
            if (isMountedRef.current) {
                setIsConnecting(false);
            }
        }
    };

    const getStatusMessage = () => {
        if (isESP32Extension && connectionType === 'mock') {
            switch (connectionStatus) {
                case 'connecting':
                    return 'Mock ESP32\'ye bağlanıyor...';
                case 'connected':
                    return 'Mock ESP32 bağlandı! Test blokları kullanabilirsiniz.';
                default:
                    return 'Mock ESP32 bağlantısı için "Bağlan" butonuna tıklayın.';
            }
        }
        
        switch (connectionStatus) {
            case 'connecting':
                return 'Bağlanıyor...';
            case 'connected':
                return 'Bağlandı! Artık cihazınızla iletişim kurabilirsiniz.';
            default:
                return 'Cihazınızı USB ile bilgisayara bağlayın ve "Bağlan" butonuna tıklayın.';
        }
    };

    const getStatusColor = () => {
        switch (connectionStatus) {
            case 'connecting':
                return '#FFA500'; // Orange
            case 'connected':
                return '#4CAF50'; // Green
            default:
                return '#757575'; // Gray
        }
    };

    return (
        <Box className={styles.connectionContent}>
            <Box className={styles.connectionHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: getStatusColor()
                    }}></div>
                    <FormattedMessage
                        defaultMessage="Seri Port Bağlantısı"
                        description="Serial port connection header"
                        id="gui.connection.serial.header"
                    />
                </div>
            </Box>

            <Box className={styles.connectionStatus}>
                <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#f5f5f5', 
                    borderRadius: '8px',
                    marginBottom: '15px',
                    textAlign: 'center',
                    color: getStatusColor(),
                    fontWeight: 'bold'
                }}>
                    {getStatusMessage()}
                </div>
            </Box>

            {error && (
                <Box className={styles.connectionError}>
                    <div style={{
                        padding: '10px',
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        borderRadius: '4px',
                        marginBottom: '15px',
                        border: '1px solid #ffcdd2'
                    }}>
                        ⚠️ {error}
                    </div>
                </Box>
            )}

            {/* ESP32 extension için bağlantı türü seçimi */}
            {isESP32Extension && (
                <Box className={styles.connectionRow}>
                    <Box className={styles.connectionLabel}>
                        Bağlantı Türü:
                    </Box>
                    <Box className={styles.connectionInput}>
                        <select
                            value={connectionType}
                            onChange={e => setConnectionType(e.target.value)}
                            disabled={isConnecting || connectionStatus === 'connected'}
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                fontSize: '14px'
                            }}
                        >
                            <option value="mock">🤖 Mock ESP32 (Test)</option>
                            <option value="real">🔌 Gerçek ESP32</option>
                        </select>
                    </Box>
                </Box>
            )}

            {/* Baud rate sadece gerçek bağlantı için göster */}
            {(!isESP32Extension || connectionType === 'real') && (
                <Box className={styles.connectionRow}>
                    <Box className={styles.connectionLabel}>
                        <FormattedMessage
                            defaultMessage="Baud Rate:"
                            description="Baud rate label"
                            id="gui.connection.serial.baudRate"
                        />
                    </Box>
                    <Box className={styles.connectionInput}>
                        <select
                            value={baudRate}
                            onChange={e => setBaudRate(Number(e.target.value))}
                            disabled={isConnecting || connectionStatus === 'connected'}
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                fontSize: '14px'
                            }}
                        >
                            <option value={9600}>9600</option>
                            <option value={19200}>19200</option>
                            <option value={38400}>38400</option>
                            <option value={57600}>57600</option>
                            <option value={115200}>115200</option>
                        </select>
                    </Box>
                </Box>
            )}

            <Box className={styles.buttonRow}>
                {connectionStatus !== 'connected' && (
                    <Button
                        className={styles.connectionButton}
                        disabled={isConnecting}
                        onClick={handleConnect}
                        style={{
                            backgroundColor: isConnecting ? '#ccc' : '#4D97FF',
                            color: 'white',
                            padding: '10px 20px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: isConnecting ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {isConnecting ? (
                            <span>🔄 Bağlanıyor...</span>
                        ) : (
                            <span>
                                {isESP32Extension && connectionType === 'mock' ? '🤖 Mock Bağlan' : '🔌 Bağlan'}
                            </span>
                        )}
                    </Button>
                )}
                
                {connectionStatus === 'connected' && (
                    <Button
                        className={styles.connectionButton}
                        onClick={() => {
                            setConnectionStatus('disconnected');
                            setError(null);
                        }}
                        style={{
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            padding: '10px 20px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        ✅ Bağlandı
                    </Button>
                )}
                
                <Button
                    className={styles.connectionButton}
                    onClick={props.onCancel}
                    style={{
                        backgroundColor: '#757575',
                        color: 'white',
                        padding: '10px 20px',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer'
                    }}
                >
                    İptal
                </Button>
            </Box>

            {connectionStatus === 'connected' && (
                <Box style={{
                    marginTop: '15px',
                    padding: '10px',
                    backgroundColor: '#e8f5e8',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#2e7d32'
                }}>
                    💡 İpucu: Artık Scratch blokları ile {isESP32Extension && connectionType === 'mock' ? 'mock ESP32\'yi' : 'cihazınızı'} kontrol edebilirsiniz!
                </Box>
            )}
        </Box>
    );
};

SerialStep.propTypes = {
    isSerialSupported: PropTypes.bool.isRequired,
    onConnectSerial: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    extensionId: PropTypes.string,
    onConnected: PropTypes.func
};

export default SerialStep; 